import { Injectable } from '@angular/core';
import { BaseApiService } from './base-api.service';
import { CACHE_DURATIONS } from '../constants/cache.constants';
import { CACHE_KEYS } from '../constants/appointment.constants';
import { Group } from '../models/group.model';
import { PresenceEntry } from '../models/presence.model';
import {
    MemberGroupRow,
    MemberRoleRow,
    MemberPasswordStatusRow,
    MemberListRow,
    GroupRow,
    PresenceStatsRow,
    PresenceRankingRow,
} from '../models/supabase-dto.model';

type AveragePresenceStats = {
    avg_present: number;
    avg_absent: number;
    avg_excused: number;
};

@Injectable({ providedIn: 'root' })
export class MemberApiService extends BaseApiService {
    async getMemberName(
        userId: string,
        cacheDuration?: number,
    ): Promise<{ first_name: string; last_name: string }[]> {
        return this.fetchWithCache<{ first_name: string; last_name: string }[]>(
            {
                cacheKey: `memberName:${userId}`,
                cacheTimestampKey: `memberNameTimestamp:${userId}`,
                rpcName: 'get_member_name_by_user_id',
                rpcParams: { user_id: userId },
                cacheDuration,
            },
        );
    }

    async getMemberGroups(
        userId: string,
        cacheDuration?: number,
    ): Promise<number[]> {
        const groups = await this.fetchWithCache<MemberGroupRow[]>({
            cacheKey: `memberGroups:${userId}`,
            cacheTimestampKey: `memberGroupsTimestamp:${userId}`,
            rpcName: 'get_member_groups_by_user_id',
            rpcParams: { user_id: userId },
            cacheDuration,
        });
        return groups.map((g) => g.group_id);
    }

    async getMemberRoles(
        userId: string,
        cacheDuration?: number,
    ): Promise<string[]> {
        const roles = await this.fetchWithCache<MemberRoleRow[]>({
            cacheKey: `memberRoles:${userId}`,
            cacheTimestampKey: `memberRolesTimestamp:${userId}`,
            rpcName: 'get_member_roles_by_user_id',
            rpcParams: { user_id: userId },
            cacheDuration,
        });
        return roles.map((r) => r.role_name);
    }

    async getMemberPasswordStatus(
        userId: string,
        cacheDuration?: number,
    ): Promise<boolean> {
        const rows = await this.fetchWithCache<MemberPasswordStatusRow[]>({
            cacheKey: `memberPasswordStatus:${userId}`,
            cacheTimestampKey: `memberPasswordStatusTimestamp:${userId}`,
            rpcName: 'get_member_password_status',
            rpcParams: { user_id: userId },
            cacheDuration,
        });

        if (!Array.isArray(rows) || rows.length === 0) {
            throw new Error(
                'Aucune information de statut de mot de passe retournee.',
            );
        }

        return rows[0].has_default_password;
    }

    async getMembersList(): Promise<MemberListRow[]> {
        return this.fetchWithCache<MemberListRow[]>({
            cacheKey: 'memberList',
            cacheTimestampKey: 'memberListTimestamp',
            rpcName: 'get_members_list',
            cacheDuration: CACHE_DURATIONS.VERY_LONG,
        });
    }

    async getAllGroups(cacheDuration?: number): Promise<Group[]> {
        const rows = await this.fetchWithCache<GroupRow[]>({
            cacheKey: CACHE_KEYS.ALL_GROUPS,
            cacheTimestampKey: CACHE_KEYS.ALL_GROUPS_TIMESTAMP,
            rpcName: 'get_all_groups_with_virtual',
            rpcParams: {},
            cacheDuration,
        });
        return rows.map((r) => ({
            id: r.id,
            name: r.name,
            isVirtual: r.is_virtual,
        }));
    }

    async getUserStats(userId: string | null): Promise<PresenceEntry[]> {
        return this.fetchWithCache<PresenceEntry[]>({
            cacheKey: `memberStats${userId}`,
            cacheTimestampKey: `memberStatsTimestamp${userId}`,
            rpcName: 'get_member_presences_status_by_user_id',
            rpcParams: { user_id: userId },
            cacheDuration: CACHE_DURATIONS.MEDIUM,
        });
    }

    async getPresenceStatsByType(
        types?: string[],
    ): Promise<PresenceStatsRow[]> {
        const typesKey =
            types && types.length > 0 ? types.sort().join(',') : 'all';
        return this.fetchWithCache<PresenceStatsRow[]>({
            cacheKey: `presenceStatsByType:${typesKey}`,
            cacheTimestampKey: `presenceStatsByTypeTimestamp:${typesKey}`,
            rpcName: 'get_presence_stats_by_type',
            rpcParams: types && types.length > 0 ? { types_param: types } : {},
            cacheDuration: CACHE_DURATIONS.SHORT,
        });
    }

    async getPresenceRanking(): Promise<PresenceRankingRow[]> {
        return this.fetchWithCache<PresenceRankingRow[]>({
            cacheKey: 'presenceRanking',
            cacheTimestampKey: 'presenceRankingTimestamp',
            rpcName: 'get_presence_ranking',
            cacheDuration: CACHE_DURATIONS.SHORT,
        });
    }

    async getAppointmentsStats(): Promise<AveragePresenceStats[]> {
        return this.fetchWithCache<AveragePresenceStats[]>({
            cacheKey: 'appointmentsStats',
            cacheTimestampKey: 'appointmentsStatsTimestamp',
            rpcName: 'get_average_presence_status',
        });
    }

    invalidatePresenceCaches(userId: string): void {
        this.invalidateCache(
            `memberStats${userId}`,
            `memberStatsTimestamp${userId}`,
        );
    }
}
